import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import { CheckCircleFilled, ArrowRightOutlined, ThunderboltOutlined, BarChartOutlined, DollarOutlined, TeamOutlined, SafetyCertificateOutlined, GiftOutlined, MobileOutlined, SafetyOutlined } from '@ant-design/icons';

const CATEGORIES = [
  {
    id: 'biometric', icon: <ThunderboltOutlined />, color: '#1890ff', bg: '#e6f4ff',
    title: 'Biometric Attendance',
    tagline: 'Fingerprint check-in in under 1 second',
    desc: 'Replace manual registers and cards with the Mantra MFS500 fingerprint scanner. Members simply place their finger and Wellness automatically marks attendance — no staff intervention needed.',
    points: [
      'Works with Mantra MFS500 scanner',
      '1:N matching across all members',
      'Automatic duplicate prevention',
      'Offline-capable with sync on reconnect',
      'Voice confirmation on check-in',
      'Browser-based — no app install needed',
    ],
  },
  {
    id: 'attendance', icon: <BarChartOutlined />, color: '#52c41a', bg: '#f6ffed',
    title: 'Attendance Management',
    tagline: 'Track every visit, absence, and streak',
    desc: 'Get a complete picture of member attendance — daily views, date ranges, absence reports, streaks, and milestones. Filter by coach, status, or search by name.',
    points: [
      'Daily and range attendance views',
      'Absence tracking with email alerts',
      'Current streak & longest streak',
      'Attendance rate per member',
      'Milestone notifications (7,14,30,60,90 days)',
      'Export to Excel with one click',
    ],
  },
  {
    id: 'payments', icon: <DollarOutlined />, color: '#fa8c16', bg: '#fff7e6',
    title: 'Payment Tracking',
    tagline: 'Never miss a payment again',
    desc: 'Track deposits, pending fees, and payment history for every member. Record payments inline from the attendance view, generate summaries, and export reports.',
    points: [
      'Quick deposit recording from attendance table',
      'Pending amount calculation per billing cycle',
      'Multiple payment methods (cash, card, online)',
      'Weekly / monthly / quarterly summaries',
      'Per-member payment history',
      'Plan type support (daily, 3-day, membership)',
    ],
  },
  {
    id: 'health', icon: <SafetyCertificateOutlined />, color: '#eb2f96', bg: '#fff0f6',
    title: 'Health Measurements',
    tagline: 'Track member health progress over time',
    desc: 'Log and monitor key health metrics for every member. Quick weight updates from the attendance page. Full history with date-wise tracking.',
    points: [
      'Weight, height, BMI tracking',
      'Body fat percentage & muscle mass',
      'Blood pressure & heart rate',
      'Quick weight update from attendance view',
      'Measurement history per member',
      'Recorded by admin with timestamps',
    ],
  },
  {
    id: 'coaches', icon: <TeamOutlined />, color: '#722ed1', bg: '#f9f0ff',
    title: 'Coach Management',
    tagline: 'Assign, track, and reward your coaches',
    desc: 'Create coaches, assign them to members, and track performance through reward points. The more members a coach has, the more points they earn.',
    points: [
      'Create and manage coach profiles',
      'Assign coaches to members',
      'Reward points = member count × 100',
      'Filter attendance by coach',
      'Add coaches inline from consumer form',
      'Coach performance overview',
    ],
  },
  {
    id: 'notifications', icon: <GiftOutlined />, color: '#13c2c2', bg: '#e6fffb',
    title: 'Smart Notifications',
    tagline: 'Automated emails and push notifications',
    desc: 'Wellness sends the right message at the right time — birthday wishes, anniversary greetings, absence nudges, and milestone celebrations.',
    points: [
      'Birthday & anniversary email greetings',
      'Absence email alerts for missing members',
      'FCM push notifications to mobile app',
      'Attendance milestone celebrations',
      'Welcome email with login credentials',
      'Duplicate prevention via notification log',
    ],
  },
  {
    id: 'multicentre', icon: <SafetyOutlined />, color: '#001529', bg: '#f0f5ff',
    title: 'Multi-Center Management',
    tagline: 'Manage multiple branches from one login',
    desc: 'Super admins can manage all wellness centers from a single dashboard. Center admins only see their own data. Full role-based access control.',
    points: [
      'Super Admin and Center Admin roles',
      'Center-level data isolation',
      'Create and manage wellness centers',
      'Assign center admins per branch',
      'Cross-center consumer management',
      'Center-scoped biometric templates',
    ],
  },
  {
    id: 'mobile', icon: <MobileOutlined />, color: '#f5222d', bg: '#fff1f0',
    title: 'Mobile App for Members',
    tagline: 'Members stay engaged with their own app',
    desc: 'Consumers get a Flutter-based mobile app to view attendance history, health measurements, payment status, and receive push notifications.',
    points: [
      'Attendance history & streaks',
      'Health measurement logs',
      'Payment records',
      'FCM push notifications',
      'Secure login with email + password',
      'Birthday & milestone celebrations',
    ],
  },
];

const Features = () => {
  // Read hash from URL on mount and when hash changes
  const [active, setActive] = useState('biometric');
  
  React.useEffect(() => {
    // Get hash from URL (e.g., #attendance)
    const hash = window.location.hash.replace('#', '');
    
    // If hash matches a valid feature, set it as active
    if (hash && CATEGORIES.some(c => c.id === hash)) {
      setActive(hash);
      
      // Scroll to the feature explorer section smoothly
      setTimeout(() => {
        const explorerSection = document.querySelector('.feat-tabs-wrap');
        if (explorerSection) {
          explorerSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  // Listen for hash changes
  React.useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && CATEGORIES.some(c => c.id === hash)) {
        setActive(hash);
      }
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const current = CATEGORIES.find(c => c.id === active);

  return (
    <>
      <style>{`
        .feat-hero {
          background: #001529; padding: 96px 32px 72px; text-align: center;
          position: relative; overflow: hidden;
        }
        .feat-hero-blob {
          position: absolute; width: 600px; height: 600px; border-radius: 50%;
          background: radial-gradient(circle, rgba(24,144,255,0.12) 0%, transparent 70%);
          top: 50%; left: 50%; transform: translate(-50%,-50%);
          pointer-events: none;
        }
        .feat-hero-inner { position: relative; z-index: 1; max-width: 680px; margin: 0 auto; }
        .feat-badge {
          display: inline-flex; align-items: center; gap: 7px;
          background: rgba(24,144,255,0.15); border: 1px solid rgba(24,144,255,0.30);
          border-radius: 50px; padding: 6px 14px; margin-bottom: 22px;
          font-size: 12.5px; font-weight: 600; color: #1890ff;
        }
        .feat-hero h1 {
          font-size: 46px; font-weight: 900; color: #fff;
          letter-spacing: -1.2px; line-height: 1.1; margin-bottom: 18px;
        }
        .feat-hero h1 span { color: #1890ff; }
        .feat-hero p { font-size: 15.5px; color: rgba(255,255,255,0.62); line-height: 1.75; }

        .pub-section      { padding: 88px 32px; }
        .pub-section-alt  { background: #f8fafc; }
        .pub-section-inner{ max-width: 1200px; margin: 0 auto; }
        .section-eyebrow  { font-size: 12px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #1890ff; margin-bottom: 12px; display: block; }
        .section-title    { font-size: 36px; font-weight: 800; color: #001529; letter-spacing: -0.8px; line-height: 1.2; margin-bottom: 48px; }

        /* Tab explorer */
        .feat-tabs-wrap {
          display: grid; grid-template-columns: 280px 1fr; gap: 32px; align-items: start;
        }
        .feat-tab-list {
          display: flex; flex-direction: column; gap: 6px;
          position: sticky; top: 88px;
        }
        .feat-tab {
          display: flex; align-items: center; gap: 12px;
          padding: 13px 16px; border-radius: 12px; cursor: pointer;
          border: 1.5px solid transparent;
          transition: all .18s; background: transparent;
          text-align: left;
        }
        .feat-tab:hover { background: #f1f5f9; }
        .feat-tab.active { background: #001529; border-color: #001529; }
        .feat-tab-icon {
          width: 36px; height: 36px; border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          font-size: 17px; flex-shrink: 0;
        }
        .feat-tab-label { font-size: 13.5px; font-weight: 600; color: #334155; }
        .feat-tab.active .feat-tab-label { color: #fff; }

        /* Detail panel */
        .feat-detail {
          background: #fff; border-radius: 20px; padding: 40px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 24px rgba(0,21,41,0.07);
        }
        .feat-detail-icon {
          width: 60px; height: 60px; border-radius: 16px;
          display: flex; align-items: center; justify-content: center;
          font-size: 26px; margin-bottom: 20px;
        }
        .feat-detail h2 { font-size: 26px; font-weight: 800; color: #1e293b; margin-bottom: 6px; letter-spacing: -0.4px; }
        .feat-detail-tagline { font-size: 14px; font-weight: 600; color: #1890ff; margin-bottom: 18px; display: block; }
        .feat-detail-desc { font-size: 14.5px; color: #475569; line-height: 1.78; margin-bottom: 28px; }
        .feat-points { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .feat-point {
          display: flex; align-items: flex-start; gap: 9px;
          font-size: 13.5px; color: #334155; font-weight: 500;
        }

        /* All features grid */
        .all-feat-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 20px; }
        .all-feat-card {
          background: #fff; border-radius: 14px; padding: 22px;
          border: 1px solid #e8edf2; text-align: center;
          transition: box-shadow .2s, transform .2s;
        }
        .all-feat-card:hover { box-shadow: 0 8px 24px rgba(0,21,41,0.09); transform: translateY(-3px); }
        .all-feat-icon { width: 44px; height: 44px; border-radius: 11px; display: flex; align-items: center; justify-content: center; font-size: 20px; margin: 0 auto 12px; }
        .all-feat-card h4 { font-size: 13.5px; font-weight: 700; color: #1e293b; margin-bottom: 6px; }
        .all-feat-card p  { font-size: 12px; color: #64748b; line-height: 1.6; margin: 0; }

        /* CTA */
        .feat-cta { background: #001529; padding: 80px 32px; text-align: center; }
        .feat-cta h2 { font-size: 36px; font-weight: 900; color: #fff; letter-spacing: -0.8px; margin-bottom: 14px; }
        .feat-cta p  { font-size: 15px; color: rgba(255,255,255,0.60); margin-bottom: 32px; }

        @media (max-width: 1024px) { .all-feat-grid { grid-template-columns: repeat(2,1fr); } }
        @media (max-width: 768px) {
          .feat-tabs-wrap { grid-template-columns: 1fr; }
          .feat-tab-list  { flex-direction: row; flex-wrap: wrap; position: static; }
          .feat-tab       { flex: 1 1 140px; }
          .feat-points    { grid-template-columns: 1fr; }
          .feat-hero h1   { font-size: 32px; }
          .section-title  { font-size: 26px; }
        }
        @media (max-width: 480px) {
          .all-feat-grid { grid-template-columns: 1fr; }
          .feat-tab      { flex: 1 1 100%; }
        }
      `}</style>

      {/* Hero */}
      <section className="feat-hero">
        <div className="feat-hero-blob" />
        <div className="feat-hero-inner">
          <div className="feat-badge"><ThunderboltOutlined /> Full Feature Suite</div>
          <h1>Everything you need to run a <span>modern wellness center</span></h1>
          <p>8 powerful modules — biometrics, payments, health, coaches, notifications — all in one platform.</p>
        </div>
      </section>

      {/* Interactive feature explorer */}
      <section className="pub-section">
        <div className="pub-section-inner">
          <span className="section-eyebrow">Feature Explorer</span>
          <h2 className="section-title">Explore every module in detail</h2>
          <div className="feat-tabs-wrap">
            {/* Tab list */}
            <div className="feat-tab-list">
              {CATEGORIES.map(c => (
                <button
                  key={c.id}
                  className={`feat-tab ${active === c.id ? 'active' : ''}`}
                  onClick={() => {
                    setActive(c.id);
                    window.location.hash = c.id;
                  }}
                >
                  <div className="feat-tab-icon" style={{ background: active === c.id ? 'rgba(255,255,255,0.12)' : c.bg, color: active === c.id ? '#fff' : c.color }}>
                    {c.icon}
                  </div>
                  <span className="feat-tab-label">{c.title}</span>
                </button>
              ))}
            </div>

            {/* Detail panel */}
            {current && (
              <div className="feat-detail">
                <div className="feat-detail-icon" style={{ background: current.bg, color: current.color }}>
                  {current.icon}
                </div>
                <h2>{current.title}</h2>
                <span className="feat-detail-tagline">✦ {current.tagline}</span>
                <p className="feat-detail-desc">{current.desc}</p>
                <div className="feat-points">
                  {current.points.map((p, i) => (
                    <div key={i} className="feat-point">
                      <CheckCircleFilled style={{ color: '#1890ff', fontSize: 15, marginTop: 2, flexShrink: 0 }} />
                      <span>{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* All features grid */}
      <section className="pub-section pub-section-alt">
        <div className="pub-section-inner">
          <span className="section-eyebrow">At a Glance</span>
          <h2 className="section-title">All modules, summarized</h2>
          <div className="all-feat-grid">
            {CATEGORIES.map((c, i) => (
              <div 
                key={i} 
                className="all-feat-card" 
                onClick={() => { 
                  setActive(c.id); 
                  window.location.hash = c.id;
                  window.scrollTo({ top: 400, behavior: 'smooth' }); 
                }} 
                style={{ cursor: 'pointer' }}
              >
                <div className="all-feat-icon" style={{ background: c.bg, color: c.color }}>{c.icon}</div>
                <h4>{c.title}</h4>
                <p>{c.tagline}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="feat-cta">
        <h2>Ready to see it in action?</h2>
        <p>Start using Wellness today — setup takes less than 30 minutes.</p>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button type="primary" size="large" style={{ borderRadius: 12, background: '#1890ff', borderColor: '#1890ff', fontWeight: 700, height: 50, padding: '0 30px', fontSize: 15, boxShadow: '0 6px 20px rgba(24,144,255,0.40)' }}>
            <Link to="/contact" style={{ color: 'inherit', textDecoration: 'none' }}>Get Started <ArrowRightOutlined /></Link>
          </Button>
          <Button size="large" style={{ borderRadius: 12, background: 'transparent', border: '1.5px solid rgba(255,255,255,0.25)', color: '#fff', fontWeight: 600, height: 50, padding: '0 30px', fontSize: 15 }}>
            <Link to="/login" style={{ color: 'inherit', textDecoration: 'none' }}>Admin Login</Link>
          </Button>
        </div>
      </section>
    </>
  );
};

export default Features;
